import { useMemo } from 'react';
import { Entity, RELATION_PART_OF, parseEntityRef } from '@backstage/catalog-model';
import { EntityFilter } from '@backstage/plugin-catalog-react';
import { useUserEntity } from './useUserEntity';

/**
 * Catalog entity filter that restricts results to the provided set of system entity names.
 */
export class AllowedSystemsEntityFilter implements EntityFilter {
  private readonly allowed: Set<string>;

  constructor(allowedSystems: string[]) {
    this.allowed = new Set(allowedSystems.map(system => system.toLowerCase()));
  }

  private extractSystem(entity: Entity): string | undefined {
    if (entity.kind?.toLowerCase() === 'system') {
      return entity.metadata?.name?.toLowerCase();
    }

    const specSystem = (entity.spec as { system?: string } | undefined)?.system;
    if (typeof specSystem === 'string' && specSystem.length > 0) {
      try {
        return parseEntityRef(specSystem).name.toLowerCase();
      } catch {
        return specSystem.toLowerCase();
      }
    }

    const partOfRelation = entity.relations?.find(relation => relation.type === RELATION_PART_OF);
    if (partOfRelation) {
      try {
        const ref = parseEntityRef(partOfRelation.targetRef);
        if (ref.kind?.toLowerCase() === 'system') {
          return ref.name.toLowerCase();
        }
      } catch {
        return undefined;
      }
    }

    return undefined;
  }

  filterEntity(entity: Entity): boolean {
    if (this.allowed.size === 0) {
      return true;
    }

    const systemName = this.extractSystem(entity);
    if (!systemName) {
      return true;
    }

    return this.allowed.has(systemName);
  }
}

export const useAllowedSystemsEntityFilter = () => {
  const {
    value: userEntity,
    loading,
    error,
  } = useUserEntity();

  const allowedSystems = useMemo(() => {
    const annotation =
      userEntity?.metadata?.annotations?.['backstage.io/allowed-systems'];

    if (!annotation) {
      return undefined;
    }

    const systems = annotation
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => {
        try {
          return parseEntityRef(item).name.toLowerCase();
        } catch {
          return item.toLowerCase();
        }
      });

    const unique = Array.from(new Set(systems));

    return unique.length > 0 ? unique : undefined;
  }, [userEntity]);

  const filter = useMemo(() => {
    if (!allowedSystems || allowedSystems.length === 0) {
      return undefined;
    }

    return new AllowedSystemsEntityFilter(allowedSystems);
  }, [allowedSystems]);

  return {
    userEntity,
    loading,
    error,
    allowedSystems,
    filter,
  };
};
